/**
 * What a person points at for the agent (F96): something an agent or a person made, named by
 * the reference the agent already reads, and the exact text selected in it. No reference
 * form of its own: `reference` is one the server and the agent's tools take, a version or
 * segment reference (`read_source_text`), a library item's `<item id>@<version>`, a Thread
 * entry `<thread id>#<entry>` (a mention) or an ontology term's IRI (`read_term_cards`).
 */
export interface ChatReference {
  readonly reference: string;
  /** How the chip names it, as the owner of the thing names it: a term's label, a document's key, an item's name; never an ID. */
  readonly name: string;
  /** The text selected in it, or `null` when the whole thing is meant. */
  readonly quote: string | null;
}

/**
 * The longest quote a reference carries. A selection over a whole document would crowd out
 * the question; the reference still leads the agent to all of it (provisional, F96).
 */
export const MAXIMUM_QUOTE_CHARACTERS = 1000;

/** The reference for a selection: its text in one line per line, cut to the maximum. */
export function referenceWithQuote(subject: Omit<ChatReference, "quote">, selected: string): ChatReference {
  const text = selected.replace(/\r\n?/g, "\n").replace(/[ \t]+/g, " ").replace(/\n{2,}/g, "\n").trim();
  const quote = text.length > MAXIMUM_QUOTE_CHARACTERS ? `${text.slice(0, MAXIMUM_QUOTE_CHARACTERS - 1)}…` : text;
  return { ...subject, quote: quote === "" ? null : quote };
}

/** Two references point at the same thing and text: adding one twice keeps one. */
export function sameChatReference(left: ChatReference, right: ChatReference): boolean {
  return left.reference === right.reference && left.quote === right.quote;
}

/**
 * The message the agent reads: the person's text, then each reference on its own line with
 * its quote as a Markdown quote below it. Entry references in it become mentions as typed
 * ones do (`mentionsOf`).
 */
export function messageWithReferences(text: string, references: readonly ChatReference[]): string {
  const blocks = references.map((reference) =>
    reference.quote === null ? reference.reference : `${reference.reference}\n${reference.quote.split("\n").map((line) => `> ${line}`).join("\n")}`,
  );
  return [text.trim(), ...blocks].filter((block) => block !== "").join("\n\n");
}
